import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import UserManagement from '../components/AdminDashboardComponents/UserManagement/UserManagement';
import ProductManagement from '../components/AdminDashboardComponents/ProductManagement/ProductManagement';
import ProductRegisterForm from '../components/AdminDashboardComponents/ProductManagement/ProductRegisterForm';
import ProductDetails from "../components/AdminDashboardComponents/ProductManagement/ProductDetails";
// import ProductAnalytics from "../components/AdminDashboardComponents/Analytics/ProductAnalytics";
import ProductBatchPage from '../components/AdminDashboardComponents/ProductBatchManagement/ProductBatchPage';
import BatchViewPage from '../components/AdminDashboardComponents/ProductBatchManagement/BatchViewPage';
import DashboardOverview from '../components/AdminDashboardComponents/DashboardOverview/DashboardOverview';


import { 
  Users, Package, Settings, BarChart3, Plus, UserPlus, CheckCircle, 
  Clock, Menu, Home, LogOut, Bell, Search, ChevronRight, TrendingUp, 
  Layers, ClipboardList, History, Activity,
  Boxes
} from 'lucide-react';
import { logout } from '../utils/auth';
import axios from 'axios';
import { API_BASE_LOCAL_URL } from '../config';
import AuditPage from './admin/AuditPage';
import AnalyticsDashboard from '../components/AdminDashboardComponents/AnalyticsDashboard/AnalyticsDashboard';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState({ role: '', username: '' });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const userManagementRef = useRef();

  const [totalUsers, setTotalUsers] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);

  // ---------------- Fetch current user ----------------
  useEffect(() => {
    const username = localStorage.getItem('username') || 'Guest';
    const role = localStorage.getItem('role') || 'Unknown';
    setCurrentUser({ role, username });
  }, []);

  // ---------------- Fetch total counts ----------------
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const fetchCounts = async () => {
      try {
        const productsRes = await axios.get(`${API_BASE_LOCAL_URL}/api/products/count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTotalProducts(productsRes.data);

        if (currentUser.role === "ADMIN" || currentUser.role === "PRODUCT_MANAGER") {
          const usersRes = await axios.get(`${API_BASE_LOCAL_URL}/api/users/count`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setTotalUsers(usersRes.data);
        }
          const batchesRes = await axios.get(`${API_BASE_LOCAL_URL}/api/batch-records/count`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setTotalBatches(batchesRes.data);
      } catch (err) {
        console.error("Error fetching counts:", err);
      }
    };

    fetchCounts();
  }, [currentUser.role]);

  // ---------------- Navigation Config ----------------
  const tabs = [
    { id: 'dashboard', icon: Home, label: 'Dashboard', path: '/AdminDashboard' },
    ...(currentUser.role === 'ADMIN' ? [{ id: 'users', icon: Users, label: 'User Management', path: '/AdminDashboard/users' }] : []),
    { id: 'products', icon: Package, label: 'Product Management', path: '/AdminDashboard/products' },
    { id: 'batch-records', icon: Boxes, label: 'Batch Records', path: '/AdminDashboard/batches' },
    { id: 'analytics', icon: BarChart3, label: 'Analytics', path: '/AdminDashboard/analytics' },
    ...(currentUser.role === 'ADMIN' ? [{ id: 'activity-logs', icon: Activity, label: 'Activity Logs', path: '/AdminDashboard/activity-logs' }] : []),
    { id: 'settings', icon: Settings, label: 'Settings', path: '/AdminDashboard/settings' },
  ];

  const activeTab = tabs.find(tab => 
    tab.path === '/AdminDashboard' 
      ? location.pathname === tab.path 
      : location.pathname.startsWith(tab.path)
  ) || tabs[0];

  const handleOpenUserModal = () => {
    if (userManagementRef.current) userManagementRef.current.openUserModal();
  };

  return (
    <div className="d-flex vh-100 bg-light-subtle">
      {/* Sidebar */}
      <aside className={`bg-dark text-white d-flex flex-column transition-all shadow`}
             style={{ width: sidebarCollapsed ? '70px' : '220px', transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }}>
        
        <div className="p-3 d-flex align-items-center justify-content-center border-bottom border-secondary border-opacity-10">
          <img
            src="/images/Navbar/Orgavitaz_logo.png"
            alt="Logo"
            style={{ width: '100%', maxWidth: sidebarCollapsed ? '30px' : '110px', filter: 'brightness(0) invert(1)' }}
          />
        </div>

        <nav className="flex-fill pt-2 px-2 overflow-y-auto">
          {tabs.map(item => (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`w-100 d-flex align-items-center mb-1 border-0 rounded-2 transition-all text-start bg-transparent ${
                activeTab.id === item.id 
                  ? 'bg-success text-white shadow-sm' 
                  : 'text-secondary hover-bg-dark-light'
              }`}
              style={{ 
                padding: '8px 12px', // Reduced from p-3 to a more compact sizing
                transition: '0.2s ease'
              }}
            >
              <item.icon size={14} />
              {!sidebarCollapsed && <span className="ms-2 fw-medium" style={{ fontSize: '0.82rem', letterSpacing: '0.3px' }}>{item.label}</span>}
            </button>
          ))}
        </nav>
        <style>{`
          .hover-bg-dark-light:hover {
            background-color: rgba(255, 255, 255, 0.05) !important;
            color: #fff !important;
          }
          /* Hide scrollbar for a cleaner look */
          nav::-webkit-scrollbar { width: 0px; }
        `}</style>

        <div className="p-3 border-top border-secondary border-opacity-10">
           {!sidebarCollapsed && <small className=" d-block mb-2 px-2" style={{ fontSize: '0.7rem', opacity: 0.6, letterSpacing: '0.5px' }}>v2.1.0-stable</small>}
           <button 
            onClick={logout} 
            className={`btn btn-outline-danger w-100 d-flex align-items-center rounded-2 border-0 ${sidebarCollapsed ? 'justify-content-center' : 'px-3'}`}
            style={{ 
              padding: '8px 0', 
              fontSize: '0.82rem',
              backgroundColor: 'rgba(220, 53, 69, 0.05)' // Subtle background for professional look
            }}
          >
          <LogOut size={14} />
          {!sidebarCollapsed && <span className="ms-2 fw-medium">Logout</span>}
        </button>
        </div>
      </aside>

      {/* Main Wrapper */}
      <div className="flex-fill d-flex flex-column overflow-hidden">
        
        {/* Modern Header */}
        <header className="bg-white border-bottom px-4 py-2 d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <button className="btn btn-light rounded-circle p-2" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
              <Menu size={20} />
            </button>
            <div className="d-none d-md-flex position-relative">
              <Search className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={16} />
              <input type="text" className="form-control form-control-sm ps-5 bg-light border-0" placeholder="Search data..." style={{ width: '300px', borderRadius: '20px' }} />
            </div>
          </div>

          <div className="d-flex align-items-center gap-4">
            <div className="position-relative cursor-pointer" onClick={() => setNotificationsOpen(!notificationsOpen)}>
              <Bell size={20} className="text-muted" />
              <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.6rem' }}>3</span>
            </div>

            <div className="vr text-muted opacity-25" style={{ height: '30px' }}></div>

            <div className="d-flex align-items-center gap-3">
              <div className="text-end d-none d-sm-block">
                <div className="fw-bold small mb-0">{currentUser.username}</div>
                <div className="text-success extra-small fw-bold text-uppercase">{currentUser.role}</div>
              </div>
              <div className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center fw-bold"
                   style={{ width: '40px', height: '40px', fontSize: '0.9rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                {currentUser.username.charAt(0).toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        {/* Dynamic Page Header / Breadcrumbs */}
        <div className="px-4 pt-4 pb-2">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-1">
              <li className="breadcrumb-item small"><Link to="/AdminDashboard" className="text-decoration-none">Admin</Link></li>
              {location.pathname !== '/AdminDashboard' && (
                <li className="breadcrumb-item small active text-capitalize">{activeTab.id}</li>
              )}
            </ol>
          </nav>
        </div>

        {/* Scrollable Body */}
        <main className="flex-fill overflow-auto p-4 pt-2">
          <Routes>
            <Route path="/" element={
             <DashboardOverview 
             totalUsers={totalUsers}
             totalProducts={totalProducts}
             totalBatches={totalBatches}
             currentUser={currentUser}
             />
            }/>

            <Route path="users" element={<UserManagement ref={userManagementRef} currentUser={currentUser} />} />
            <Route path="products/*" element={<Outlet />}>
                <Route index element={<ProductManagement />} />
                <Route path="add" element={<ProductRegisterForm />} />
                <Route path=":productCode/edit" element={<ProductRegisterForm />} />
                <Route path=":productCode" element={<ProductDetails />} />
            </Route>

            <Route path="batches" element={<ProductBatchPage />} />
            <Route path="batch-view/:id" element={<BatchViewPage />} />

            <Route path="analytics" element={<AnalyticsDashboard />} />
            <Route path="activity-logs" element={<AuditPage currentUser={currentUser} />} />

            <Route path="settings" element={
              <div className="card border-0 shadow-sm p-4">
                <h5 className="fw-bold mb-3">Settings</h5>
                <hr />
                <div className="row">
                   <div className="col-md-6">
                      <label className="form-label fw-medium">Notification Email</label>
                      <input type="email" className="form-control mb-3" placeholder="admin@orgavitaz.com" />
                      <button className="btn btn-success">Update Preferences</button>
                   </div>
                </div>
              </div>
            }/>
          </Routes>
        </main>
      </div>
    </div>
  );
}